import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/context/AppContext";
import { PrimaryBtn, GhostBtn } from "@/pages/app/Dashboard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const TYPES = ["interview_completed", "offer_accepted", "contract_completed", "offer_rejected", "no_show", "misconduct"];
const NEGATIVE = ["no_show", "misconduct"];

const propagationText = (p, t) => {
  if (p.type === "trust") return `${p.name}: ${t("trust")} ${p.from} → ${p.to}`;
  if (p.type === "intent") return `intent → fulfilled · openings left ${p.company_openings_left}`;
  return `rel ${p.relationship_id.slice(-6)}: ${p.from} → ${p.to} (${p.reason})`;
};

export const OutcomePanel = ({ r, onSaved }) => {
  const { t } = useLang();
  const [f, setF] = useState({ type: "interview_completed", rating: 4, subject_principal_id: "", notes: "" });
  const [last, setLast] = useState(null);
  const submit = async () => {
    try {
      const body = { ...f, rating: Number(f.rating) || null, subject_principal_id: NEGATIVE.includes(f.type) ? f.subject_principal_id : null };
      const { data } = await api.post(`/relationships/${r.id}/outcomes`, body);
      setLast(data.outcome);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-md border border-white/[0.08] bg-[#111827] p-6" data-testid="outcome-panel">
      <p className="mb-4 font-mono text-[11px] uppercase tracking-widest text-slate-500">{t("outcomes")}</p>
      {r.state === "active" && (
        <div className="space-y-3">
          <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
            <SelectTrigger data-testid="outcome-type-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827]">{TYPES.map((x) => <SelectItem key={x} value={x} data-testid={`outcome-type-${x}`}>{t(`oc_${x}`)}</SelectItem>)}</SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1 text-xs text-slate-400">{t("rating")}<Input type="number" min={1} max={5} value={f.rating} onChange={(e) => setF({ ...f, rating: e.target.value })} data-testid="outcome-rating-input" /></label>
            {NEGATIVE.includes(f.type) && (
              <label className="space-y-1 text-xs text-slate-400">{t("responsible")}
                <Select value={f.subject_principal_id} onValueChange={(v) => setF({ ...f, subject_principal_id: v })}>
                  <SelectTrigger data-testid="outcome-subject-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#111827]">
                    <SelectItem value={r.person_principal_id} data-testid="outcome-subject-person">{r.person.name}</SelectItem>
                    <SelectItem value={r.company_principal_id} data-testid="outcome-subject-company">{r.company.name}</SelectItem>
                  </SelectContent>
                </Select>
              </label>
            )}
          </div>
          <Input placeholder={t("notes")} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="outcome-notes-input" />
          <PrimaryBtn onClick={submit} data-testid="register-result-btn">{t("register_result")}</PrimaryBtn>
        </div>
      )}
      {last && !!last.propagation.length && (
        <div className="mt-4 rounded border border-cyan-400/30 p-3" data-testid="propagation-result">
          <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">{t("propagation")}</p>
          {last.propagation.map((p, i) => (
            <p key={`${p.type}-${p.principal_id || p.relationship_id || "intent"}`} className="font-mono text-[11px] text-slate-300">{propagationText(p, t)}</p>
          ))}
        </div>
      )}
      <ul className="mt-4 space-y-2">
        {r.outcomes.map((o) => (
          <li key={o.id} className="flex justify-between border-t border-white/5 pt-2 text-xs" data-testid={`outcome-item-${o.id}`}>
            <span className={NEGATIVE.includes(o.type) ? "text-rose-300" : "text-slate-200"}>{t(`oc_${o.type}`)} {o.rating ? `· ${o.rating}/5` : ""}</span>
            <span className="font-mono text-slate-500">{t("cycle")} {o.cycle}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const ReauthPanel = ({ r, onDone }) => {
  const { t } = useLang();
  const [res, setRes] = useState(null);
  const check = async () => { try { const { data } = await api.get(`/relationships/${r.id}/reauth-eligibility`); setRes(data); } catch (e) { toast.error(errMsg(e)); } };
  const reauth = async () => { try { await api.post(`/relationships/${r.id}/reauthorize`); setRes(null); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-md border border-white/[0.08] bg-[#111827] p-6" data-testid="reauth-panel">
      <p className="mb-4 font-mono text-[11px] uppercase tracking-widest text-slate-500">{t("reauth")}</p>
      <GhostBtn onClick={check} data-testid="check-reauth-btn">{t("check_reauth")}</GhostBtn>
      {res && (
        <div className="mt-4 space-y-2" data-testid="reauth-result">
          <p className={`font-display text-xl font-bold ${res.eligible ? "text-emerald-300" : "text-rose-300"}`} data-testid="reauth-eligibility-status">{t(res.eligible ? "eligible_reauth" : "not_eligible_reauth")}</p>
          <p className="font-mono text-[11px] text-slate-400">state={res.checks.state} · outcomes={res.checks.outcomes} · avg={res.checks.average_rating ?? "—"} · score={res.checks.intersection_score}</p>
          {res.reasons.map((x) => <p key={x} className="font-mono text-[11px] text-rose-300">• {x}</p>)}
          {res.eligible && <PrimaryBtn onClick={reauth} data-testid="do-reauth-btn">{t("do_reauth")}</PrimaryBtn>}
        </div>
      )}
    </div>
  );
};
